import React, { useState, useEffect } from 'react'
import { Upload, message, Modal } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'
import { apiClient } from '@/shared/api/axios-instance'

interface DocumentUploadProps {
    value?: string | string[]
    onChange?: (value: string | string[] | undefined) => void
    onUploadingChange?: (isUploading: boolean) => void
    documentType?: string
    maxSize?: number // in bytes
    multiple?: boolean
    maxCount?: number
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
    value,
    onChange,
    onUploadingChange,
    documentType = 'category_image',
    maxSize = 10 * 1024 * 1024, // 10MB default
    multiple = false,
    maxCount = 1
}) => {
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewImage, setPreviewImage] = useState('')
    const [previewTitle, setPreviewTitle] = useState('')

    // Sync value prop to fileList
    useEffect(() => {
        if (!value) {
            setFileList(prev => {
                const uploadingFiles = prev.filter(f => f.status === 'uploading')
                // If there are uploading files or if it's already empty, don't change to avoid loops
                if (uploadingFiles.length > 0 && uploadingFiles.length === prev.length) return prev
                if (prev.length === 0) return prev
                return uploadingFiles
            })
            return
        }

        const ids = Array.isArray(value) ? value : [value]

        setFileList(prev => {
            const newFileList = ids.map(id => {
                // Find existing to avoid destroying local preview URLs
                const existing = prev.find(f => f.uid === id || f.response?.uuid === id)
                if (existing) {
                    return existing
                }

                // New item from parent (e.g. initial load in edit mode)
                return {
                    uid: id,
                    name: `Document`,
                    status: 'done',
                } as UploadFile
            })

            const uploadingFiles = prev.filter(f => f.status === 'uploading')

            // Avoid unnecessary state updates
            const isSame = newFileList.length === prev.length - uploadingFiles.length &&
                newFileList.every((f, i) => f.uid === prev[i]?.uid)

            if (isSame) return prev;

            return [...newFileList, ...uploadingFiles]
        })
    }, [value])

    const isUuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

    // Fetch URLs for existing UUIDs that don't have a preview URL yet
    useEffect(() => {
        fileList.forEach(file => {
            // If it is 'done', has a valid UUID, and no url/preview yet
            if (file.status === 'done' && !file.url && !file.preview && isUuid(file.uid)) {
                // To prevent infinite loops, we can set a temporary preview to mark it as fetching
                file.preview = 'loading'

                apiClient.get(`/documents/${file.uid}/download-url`)
                    .then(res => {
                        if (res.data?.success) {
                            const url = res.data.data.download_url || res.data.data.cdn_url
                            setFileList(prev => prev.map(f => f.uid === file.uid ? { ...f, url, preview: undefined } : f))
                        } else {
                            // Reset preview so it doesn't show loading forever if failed
                            file.preview = undefined
                        }
                    })
                    .catch(err => {
                        console.error("Failed to load document preview:", err)
                        file.preview = undefined
                    })
            } else if (file.status === 'done' && !file.url && !isUuid(file.uid)) {
                // If it's done and not a UUID, it's likely already a URL
                setFileList(prev => prev.map(f => f.uid === file.uid ? { ...f, url: file.uid } : f))
            }
        })
    }, [fileList])

    // Notify parent component if any file is currently uploading
    useEffect(() => {
        const isUploading = fileList.some(f => f.status === 'uploading')
        onUploadingChange?.(isUploading)
    }, [fileList, onUploadingChange])

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await new Promise((resolve) => {
                const reader = new FileReader()
                reader.readAsDataURL(file.originFileObj as Blob)
                reader.onload = () => resolve(reader.result as string)
            })
        }

        setPreviewImage(file.url || (file.preview as string))
        setPreviewOpen(true)
        setPreviewTitle(file.name || file.url?.substring(file.url.lastIndexOf('/') + 1) || 'Preview')
    }

    const uploadFile = async (file: File): Promise<string> => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            throw new Error('Please upload an image file')
        }

        // Validate file size
        if (file.size > maxSize) {
            throw new Error(`File size must be less than ${maxSize / 1024 / 1024}MB`)
        }

        // Step 1: Request upload intent
        const intentResponse = await apiClient.post('/documents/upload-intent', {
            filename: file.name,
            mime_type: file.type,
            file_size: file.size,
            document_type: documentType,
            visibility: 'public',
        })

        const { session_id, presigned_url } = intentResponse.data.data

        // Step 2: Upload to S3
        const uploadResponse = await fetch(presigned_url, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
            },
            body: file,
        })

        if (!uploadResponse.ok) {
            throw new Error('Failed to upload file to S3')
        }

        // Step 3: Confirm upload
        const confirmResponse = await apiClient.post('/documents/confirm-upload', {
            session_id,
        })

        return confirmResponse.data.data.uuid
    }

    const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
        try {
            const uuid = await uploadFile(file as File)
            const localUrl = URL.createObjectURL(file as File)
            onSuccess?.({ uuid, url: localUrl }, file as any)
        } catch (error: any) {
            message.error(error.message || 'Upload failed')
            onError?.(error)
        }
    }

    const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
        // Map response data onto the file object to persist preview and ID
        const updatedList = newFileList.map(f => {
            if (f.response?.uuid) {
                return { ...f, uid: f.response.uuid, url: f.response.url || f.url }
            }
            return f
        })

        setFileList(updatedList)

        // Calculate value for parent using UIDs
        const validFiles = updatedList.filter(f => f.status === 'done' && f.uid.length > 10) // Ensure it's a UUID
        const uploadingFiles = updatedList.filter(f => f.status === 'uploading')

        // If there are valid files, emit them. If there's an active upload, don't clear the form value yet.
        // Emitting undefined triggers the validation "Please upload image".
        if (validFiles.length > 0) {
            const values = validFiles.map(f => f.uid)
            onChange?.(multiple ? values : values[0])
        } else if (uploadingFiles.length === 0) {
            // Only clear the value if nothing is uploading and no valid files exist
            onChange?.(undefined)
        }
    }

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    )

    return (
        <>
            <Upload
                listType="picture-card"
                className="avatar-uploader"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                customRequest={customRequest}
                maxCount={maxCount}
                multiple={multiple}
                accept="image/*"
            >
                {fileList.length >= maxCount ? null : uploadButton}
            </Upload>
            <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
                <img alt="example" style={{ width: '100%' }} src={previewImage} />
            </Modal>
        </>
    )
}
